import { AddressByMapbox, AddressComponent } from "@/components/AddressComponent";
import * as React from "react";

import { ArrowIcon, EditIcon } from "components/icons";
import { CalendarSelector } from "components/form-control";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ParkingMeterFormValues, ParkingMeterModalState, parkingMeterSchema } from "../parking-payment.const";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { InputNumber } from "@/components/form-control/input-number";
import { ParkingMeter } from "services/web/api-parking-meter.type";
import clsx from "clsx";
import { isNil } from "helper/utils";
import { useForm } from "react-hook-form";
import useToggle from "hooks/use-toggle";
import { zodResolver } from "@hookform/resolvers/zod";

interface ICoordinates {
  latitude: number;
  longitude: number;
}

interface ParkingMeterModalProps {
  coordinates?: ICoordinates;
  saveParkingMeter: (data: ParkingMeter) => Promise<void>;
  parkingMeter: ParkingMeter;
  cancel: (isEdit: boolean) => void;
  parkingModalState: keyof typeof ParkingMeterModalState;
  setParkingModalState: React.Dispatch<React.SetStateAction<keyof typeof ParkingMeterModalState>>;
  setParkingMeterDetail: (value: ParkingMeter | null) => void;
}

export const ParkingMeterModal = ({
  saveParkingMeter,
  parkingMeter,
  cancel,
  setParkingModalState,
  parkingModalState,
  setParkingMeterDetail,
  coordinates,
}: ParkingMeterModalProps) => {
  const form = useForm<ParkingMeterFormValues>({
    resolver: zodResolver(parkingMeterSchema),
    defaultValues: {
      ...parkingMeter,
      batteryLevel: parkingMeter.batteryLevel ?? 0,
      lastMaintenanceDate: parkingMeter?.lastMaintenanceDate ? new Date(parkingMeter?.lastMaintenanceDate) : new Date(),
      options: {
        cash: parkingMeter.options?.cash ?? false,
        creditCard: parkingMeter.options?.creditCard ?? false,
        contactless: parkingMeter.options?.contactless ?? false,
      },
    },
    mode: "all",
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = form;

  const [toggleMoreOptions, setToggleMoreOptions] = useToggle(false);

  const handleMinimizeModal = () => {
    setParkingModalState(ParkingMeterModalState.MINIMIZE);
  };

  const handleSelectPlace = ({ geometry }: AddressByMapbox) => {
    setParkingMeterDetail({
      ...parkingMeter,
      location: geometry ?? parkingMeter.location,
    });
  };

  const isEdit = !isNil(parkingMeter?.id);

  const dismiss = () => cancel(isEdit);
  const save = async (data: ParkingMeterFormValues) => {
    const formattedData = {
      ...parkingMeter,
      ...data,
      location: parkingMeter?.location,
      batteryLevel: Number(data.batteryLevel),
    };
    await saveParkingMeter(formattedData);
  };

  const handleShowModal = () => {
    setParkingModalState(ParkingMeterModalState.SHOW);
  };

  const modalMinimize = parkingModalState === ParkingMeterModalState.MINIMIZE;

  return (
    <>
      <Dialog open={!modalMinimize}>
        <DialogContent className="h-[calc(100vh-5em)] overflow-auto">
          <DialogHeader>
            <DialogTitle>{`${parkingMeter?.id ? "Edit " : "Add "}a parking meter`}</DialogTitle>
            <DialogDescription>Parking Meter information</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={handleSubmit(save)} className="space-y-6">
              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Name <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold">Location:</span>
                {parkingMeter?.location && (
                  <Button className="text-blue-500" onClick={handleMinimizeModal} variant="ghost" type="button">
                    {parkingMeter?.location.coordinates.toString()}
                  </Button>
                )}
                <EditIcon className="w-5 h-5 cursor-pointer fill-black" onClick={handleMinimizeModal} />
              </div>
              <AddressComponent onSelectAddress={handleSelectPlace} coordinates={coordinates} />

              <FormField
                control={control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="externalId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>External ID</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="operator"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Operator <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="brand"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Brand <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Model <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="zone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Zone</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="batteryLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Battery Level</FormLabel>
                    <FormControl>
                      <InputNumber {...field} min={0} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="lastMaintenanceDate"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Last Maintenance Date</FormLabel>
                    <FormControl>
                      <CalendarSelector selectedDate={value} onChangeValue={onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {toggleMoreOptions && (
                <div className="transition-all duration-500 overflow-hidden space-y-6">
                  <FormField
                    control={control}
                    name="options.cash"
                    render={({ field: { value, onChange } }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <div className="flex items-center space-x-2">
                            <Checkbox id="cash" checked={value} onCheckedChange={onChange} />
                            <label
                              htmlFor="cash"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Cash
                            </label>
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="options.creditCard"
                    render={({ field: { value, onChange } }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <div className="flex items-center space-x-2">
                            <Checkbox id="credit-card" checked={value} onCheckedChange={onChange} />
                            <label
                              htmlFor="credit-card"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Credit card
                            </label>
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="options.contactless"
                    render={({ field: { value, onChange } }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <div className="flex items-center space-x-2">
                            <Checkbox id="contactless" checked={value} onCheckedChange={onChange} />
                            <label
                              htmlFor="contactless"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Contact less
                            </label>
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <Button type="button" onClick={setToggleMoreOptions} variant="ghost">
                {toggleMoreOptions ? "Hide" : "More options"}
                <ArrowIcon className={clsx("transform size-3", toggleMoreOptions ? "rotate-90" : "-rotate-90")} />
              </Button>

              <DialogFooter className="sm:justify-between">
                <Button onClick={dismiss} variant="secondary" type="button">
                  Cancel
                </Button>
                <Button disabled={isSubmitting} type="submit">
                  Save
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <Button
        className={clsx("absolute bottom-12 right-4 shadow-md animate-bounce", modalMinimize ? "flex" : "hidden")}
        onClick={handleShowModal}
        variant="outline"
      >
        <span>Back to edit parking meter</span>
        <ArrowIcon className="w-3 h-3 rotate-90" />
      </Button>
    </>
  );
};
